import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../services/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  Box,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SyncIcon from '@mui/icons-material/Sync';

interface SyncProgressEvent {
  type: 'progress' | 'complete' | 'error';
  step: string;
  current: number;
  total: number;
  pct: number;
  errors: string[];
  summary?: {
    results: Array<{ source: string; success: boolean; metricsUpdated?: number }>;
    totalErrors: number;
    sourcesUpdated: number;
    sourcesFailed: number;
  };
}

interface SyncProgressDialogProps {
  open: boolean;
  onClose: () => void;
  jobId: string | null;
}

function formatEta(elapsedMs: number, pct: number): string {
  if (pct <= 2) return 'Calculating...';
  const totalEstimate = (elapsedMs / pct) * 100;
  const remainingMs = totalEstimate - elapsedMs;
  const remainingSec = Math.round(remainingMs / 1000);
  if (remainingSec < 10) return 'Almost done...';
  if (remainingSec < 60) return `~${remainingSec}s remaining`;
  const mins = Math.ceil(remainingSec / 60);
  return `~${mins} min remaining`;
}

const STEP_LABELS: Record<string, string> = {
  'Counting active leads': 'Counting active pipeline leads...',
  'Pulling deals won': 'Pulling closed deals...',
  'Counting deals won (duplicate)': 'Updating deals won metric...',
  'Counting leads lost': 'Counting lost leads...',
  'Pulling calls booked': 'Fetching booked calls...',
  'Pulling calls completed': 'Fetching completed calls...',
  'Pulling contracts sent': 'Fetching sent contracts...',
  'Pulling tickets created': 'Pulling support tickets...',
  'Pulling tickets completed': 'Pulling resolved tickets...',
  'Calculating ticket resolution SLA': 'Calculating on-time resolution rates...',
  'Syncing API data sources': 'Syncing API data sources...',
  'Syncing Clockify time entries': 'Downloading time entries...',
  'Calculating Clockify metrics': 'Calculating time-based metrics...',
};

export default function SyncProgressDialog({ open, onClose, jobId }: SyncProgressDialogProps) {
  const [progress, setProgress] = useState<SyncProgressEvent | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const startTimeRef = useRef<number>(Date.now());
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!open || !jobId) return;

    startTimeRef.current = Date.now();
    setIsComplete(false);
    setProgress(null);

    const es = new EventSource(`${API_BASE_URL}/metrics/update-scorecard/progress/${jobId}`);
    eventSourceRef.current = es;

    es.onmessage = (event) => {
      try {
        const data: SyncProgressEvent = JSON.parse(event.data);
        setProgress(data);
        if (data.type === 'complete' || data.type === 'error') {
          setIsComplete(true);
          es.close();
        }
      } catch (e) {
        console.error('Failed to parse SSE event:', e);
      }
    };

    es.onerror = () => {
      // SSE connection closed (normal when job completes)
      es.close();
    };

    return () => {
      es.close();
      eventSourceRef.current = null;
    };
  }, [open, jobId]);

  const elapsed = Date.now() - startTimeRef.current;
  const pct = progress?.pct || 0;
  const stepLabel = progress?.step ? (STEP_LABELS[progress.step] || progress.step) : 'Starting sync...';

  return (
    <Dialog
      open={open}
      onClose={isComplete ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        {isComplete ? (
          progress?.errors?.length ? (
            <ErrorIcon sx={{ color: '#ff6b6b' }} />
          ) : (
            <CheckCircleIcon sx={{ color: '#4caf50' }} />
          )
        ) : (
          <SyncIcon sx={{ color: 'primary.main', animation: 'spin 2s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
        )}
        {isComplete ? 'Sync Complete' : 'Syncing Data...'}
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* Progress bar */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {stepLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {pct}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              height: 10,
              borderRadius: '999px',
              bgcolor: 'rgba(201, 169, 97, 0.15)',
              '& .MuiLinearProgress-bar': {
                borderRadius: '999px',
                bgcolor: 'primary.main',
              },
            }}
          />
          {!isComplete && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Step {progress?.current || 0} of {progress?.total || '...'} — {formatEta(elapsed, pct)}
            </Typography>
          )}
        </Box>

        {/* Summary when complete */}
        {isComplete && progress?.summary && (
          <Box sx={{ mb: 2 }}>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Chip
                label={`${progress.summary.sourcesUpdated} sources updated`}
                color="success"
                size="small"
                variant="outlined"
              />
              {progress.summary.sourcesFailed > 0 && (
                <Chip
                  label={`${progress.summary.sourcesFailed} sources failed`}
                  color="error"
                  size="small"
                  variant="outlined"
                />
              )}
              {progress.summary.totalErrors > 0 && (
                <Chip
                  label={`${progress.summary.totalErrors} errors`}
                  color="warning"
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        )}

        {/* Errors */}
        {progress?.errors && progress.errors.length > 0 && (
          <Box sx={{ maxHeight: 200, overflowY: 'auto', mt: 1 }}>
            <Typography variant="subtitle2" color="error" sx={{ mb: 0.5 }}>
              Issues found:
            </Typography>
            {progress.errors.map((err, i) => (
              <Alert key={i} severity="warning" sx={{ mb: 0.5, py: 0, fontSize: '12.5px' }}>
                {err}
              </Alert>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          disabled={!isComplete}
          variant={isComplete ? 'contained' : 'text'}
          color="primary"
        >
          {isComplete ? 'Close' : 'Syncing...'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
